import { createFileRoute, Outlet } from '@tanstack/react-router';
import { getDefaultStore } from 'jotai';
import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

import { BREADCRUMB_ACTIONS_ELEMENT_ID } from '#src/routes/root-layout/app-breadcrumbs.js';
import { lastOpenProjectAtom } from '#state/atoms/last-open-project-atom.js';
import {
	persistentQueryOptions,
	persistentQueryPolicies,
} from '#utils/persistent-query-cache/persistent-query-cache.js';
import { revisionQueryOptions } from '#utils/revision-query-options.js';
import { RevisionBreadcrumbActions } from './revision-breadcrumb-actions.js';

export const Route = createFileRoute('/_authorization-gate/orgs/$orgName/projects/$projectId/revisions/$revisionId')({
	staticData: {
		authorize: (ability) => ability.can('view', 'project'),
	},
	beforeLoad: ({ params: { projectId, revisionId }, context: { trpc } }) => {
		getDefaultStore().set(lastOpenProjectAtom, (prev) => ({ ...prev, revisionId }));

		return {
			getProjectOptions: trpc.project.get.queryOptions({ id: projectId, buildId: revisionId }, { staleTime: Infinity }),
			getModuleResolutionsOptions: trpc.moduleResolution.getMany.queryOptions({ projectId, buildId: revisionId }),
			getRawProjectModelOptions: trpc.project.getRawModel.queryOptions(
				{ projectId, buildId: revisionId },
				persistentQueryOptions(persistentQueryPolicies.projectModel),
			),
			getRevisionOptions: revisionQueryOptions(trpc, revisionId),
		};
	},
	component: ProjectRevisionLayout,
});

/** The shell renders the slot once and keeps it for the life of the app, so there is nothing to subscribe to. */
const subscribeToSlot = () => () => undefined;
const readSlot = () => document.getElementById(BREADCRUMB_ACTIONS_ELEMENT_ID);
const readNoSlot = () => null;

/**
 * The revision's routes, with whatever belongs to the revision placed in the breadcrumb row's
 * far-right slot.
 */
function ProjectRevisionLayout() {
	// The slot belongs to the shell breadcrumb bar, a sibling rather than an ancestor, so its node is
	// only reachable once both have been committed — which is the re-read this gets for free.
	const slot = useSyncExternalStore(subscribeToSlot, readSlot, readNoSlot);

	return (
		<>
			{slot !== null && createPortal(<RevisionBreadcrumbActions />, slot)}
			<Outlet />
		</>
	);
}
